import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { getSalesArrayPayload, getSalesId, getSalesValue, useSalesEndpoints } from '../hooks/useSalesApiResource';

const marketOptions = [
  { value: 1, label: 'Golf' },
  { value: 2, label: 'Landscape' },
  { value: 3, label: 'Municipal' },
  { value: 4, label: 'Industrial' },
  { value: 5, label: 'Other' },
];

const normalizeDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const normalizeOrderSheet = (order = {}) => ({
  raw: order,
  id: Number(getSalesId(order) || 0),
  projectId: Number(getSalesValue(order, 'projectId', 'ProjectId') || 0),
  projectName: String(getSalesValue(order, 'projectName', 'ProjectName') || ''),
  saleQuoteId: Number(getSalesValue(order, 'saleQuoteId', 'SaleQuoteId') || 0),
  jobNumber: String(getSalesValue(order, 'jobNumber', 'JobNumber') || ''),
  drawing: Number(getSalesValue(order, 'drawing', 'Drawing') || 0),
  latePenalty: Boolean(getSalesValue(order, 'latePanelty', 'LatePanelty') ?? false),
  penaltyAmount: Number(getSalesValue(order, 'paneltyAmount', 'PaneltyAmount') || 0),
  witnessTest: Number(getSalesValue(order, 'orderSheetWitness', 'OrderSheetWitness') || 0),
  orderDate: normalizeDate(getSalesValue(order, 'orderDate', 'OrderDate')),
  requestedDeliveryDate: normalizeDate(getSalesValue(order, 'requestedDeliveryDate', 'RequestedDeliveryDate')),
  isSchedule: Boolean(getSalesValue(order, 'isSchedule', 'IsSchedule') ?? false),
  market: Number(getSalesValue(order, 'market', 'Market') || 0),
  contactPerson: String(getSalesValue(order, 'contactPerson', 'ContactPerson') || ''),
  invoiceCompanyName: String(getSalesValue(order, 'companyName', 'CompanyName') || ''),
  invoiceAddress: String(getSalesValue(order, 'address', 'Address') || ''),
  invoiceCity: String(getSalesValue(order, 'city', 'City') || ''),
  invoiceState: String(getSalesValue(order, 'state', 'State') || ''),
  invoiceZip: String(getSalesValue(order, 'zipCode', 'ZipCode') || ''),
  paymentTerms: String(getSalesValue(order, 'paymentTermsType', 'PaymentTermsType', 'paymentTermsId', 'PaymentTermsId') || ''),
  poNumber: String(getSalesValue(order, 'purchaseOrder', 'PurchaseOrder') || ''),
  poAmount: Number(getSalesValue(order, 'pbInvoicePrice', 'PBInvoicePrice') || 0),
  shipToName: String(getSalesValue(order, 'shipmentCompanyName', 'ShipmentCompanyName') || ''),
  shipToAddress: String(getSalesValue(order, 'shipmentAddress', 'ShipmentAddress') || ''),
  shipToCity: String(getSalesValue(order, 'shipmentCity', 'ShipmentCity') || ''),
  shipToState: String(getSalesValue(order, 'shipmentState', 'ShipmentState') || ''),
  shipToZip: String(getSalesValue(order, 'shipmentZipCode', 'ShipmentZipCode') || ''),
  shipToPhone: String(getSalesValue(order, 'shipmentPhone', 'ShipmentPhone') || ''),
  specialInstructions: String(getSalesValue(order, 'specialInstructions', 'SpecialInstructions') || ''),
  pbMaterialStationPrice: Number(getSalesValue(order, 'pbMaterialStationPrice', 'PBMaterialStationPrice') || 0),
  pbExtendedWarranty: Number(getSalesValue(order, 'pbExtendedWarranty', 'PBExtendedWarranty') || 0),
  pbFreight: Number(getSalesValue(order, 'pbFreight', 'PBFreight') || 0),
  pbSetupAllowance: Number(getSalesValue(order, 'pbSetUpAllowance', 'PBSetUpAllowance') || 0),
  pbStartupAllowance: Number(getSalesValue(order, 'pbStartUpAllowance', 'PBStartUpAllowance') || 0),
  pbInvoicePrice: Number(getSalesValue(order, 'pbInvoicePrice', 'PBInvoicePrice') || 0),
  rebatePercent: Number(getSalesValue(order, 'rebatePercentage', 'RebatePercentage') || 0),
  rebateAmount: Number(getSalesValue(order, 'rebateAmount', 'RebateAmount') || 0),
  commissionablePercent: 100,
  commission1Name: String(getSalesValue(order, 'commission1CompanyTobePaid', 'Commission1CompanyTobePaid') || ''),
  commission1Percent: Number(getSalesValue(order, 'commission1FindersFee1Percentage', 'Commission1FindersFee1Percentage') || 0),
  commission1Amount: Number(getSalesValue(order, 'commission1FindersFee1Amount', 'Commission1FindersFee1Amount') || 0),
  commission2Name: String(getSalesValue(order, 'commission2CompanyTobePaid', 'Commission2CompanyTobePaid') || ''),
  commission2Percent: Number(getSalesValue(order, 'commission2FindersFee2Percentage', 'Commission2FindersFee2Percentage') || 0),
  commission2Amount: Number(getSalesValue(order, 'commission2FindersFee2Amount', 'Commission2FindersFee2Amount') || 0),
});

const toApiPayload = (form) => ({
  ...form.raw,
  Id: form.id,
  ProjectId: form.projectId || null,
  ProjectName: form.projectName,
  SaleQuoteId: form.saleQuoteId || null,
  JobNumber: form.jobNumber,
  Drawing: Number(form.drawing) || 0,
  LatePanelty: form.latePenalty,
  PaneltyAmount: Number(form.penaltyAmount) || null,
  OrderSheetWitness: Number(form.witnessTest) || null,
  OrderDate: form.orderDate || null,
  RequestedDeliveryDate: form.isSchedule ? null : form.requestedDeliveryDate || null,
  IsSchedule: form.isSchedule,
  Market: Number(form.market) || null,
  ContactPerson: form.contactPerson,
  CompanyName: form.invoiceCompanyName,
  Address: form.invoiceAddress,
  City: form.invoiceCity,
  State: form.invoiceState,
  ZipCode: form.invoiceZip,
  PaymentTermsType: form.paymentTerms,
  PurchaseOrder: form.poNumber,
  ShipmentCompanyName: form.shipToName,
  ShipmentAddress: form.shipToAddress,
  ShipmentCity: form.shipToCity,
  ShipmentState: form.shipToState,
  ShipmentZipCode: form.shipToZip,
  ShipmentPhone: form.shipToPhone,
  SpecialInstructions: form.specialInstructions,
  PBMaterialStationPrice: Number(form.pbMaterialStationPrice) || 0,
  PBExtendedWarranty: Number(form.pbExtendedWarranty) || 0,
  PBFreight: Number(form.pbFreight) || 0,
  PBSetUpAllowance: Number(form.pbSetupAllowance) || 0,
  PBStartUpAllowance: Number(form.pbStartupAllowance) || 0,
  PBInvoicePrice: Number(form.pbInvoicePrice) || 0,
  RebatePercentage: Number(form.rebatePercent) || 0,
  RebateAmount: Number(form.rebateAmount) || 0,
  Commission1CompanyTobePaid: form.commission1Name,
  Commission1FindersFee1Percentage: Number(form.commission1Percent) || 0,
  Commission1FindersFee1Amount: Number(form.commission1Amount) || 0,
  Commission2CompanyTobePaid: form.commission2Name,
  Commission2FindersFee2Percentage: Number(form.commission2Percent) || 0,
  Commission2FindersFee2Amount: Number(form.commission2Amount) || 0,
});

const addressFields = [
  ['invoiceCompanyName', 'Company Name'],
  ['invoiceAddress', 'Address'],
  ['invoiceCity', 'City'],
  ['invoiceState', 'State'],
  ['invoiceZip', 'Zip'],
];

const shipToFields = [
  ['shipToName', 'Name'],
  ['shipToAddress', 'Address'],
  ['shipToCity', 'City'],
  ['shipToState', 'State'],
  ['shipToZip', 'Zip'],
  ['shipToPhone', 'Phone'],
];

const priceFields = [
  ['pbMaterialStationPrice', 'Material Station Price'],
  ['pbExtendedWarranty', 'Extended Warranty'],
  ['pbFreight', 'Freight'],
  ['pbSetupAllowance', 'Setup Allowance'],
  ['pbStartupAllowance', 'Startup Allowance'],
  ['pbInvoicePrice', 'Invoice Price'],
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);

const OrderSheetDetail = () => {
  const { id } = useParams();
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [pageError, setPageError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadOrderSheet = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const response = await request({ method: 'GET', url: endpoints.salesQuoteOrders.base });
      const order = getSalesArrayPayload(response).find((item) => Number(getSalesId(item)) === Number(id));
      if (!order) {
        setPageError('Order sheet was not found.');
        setForm(null);
        return;
      }
      setForm(normalizeOrderSheet(order));
    } catch {
      setPageError('Unable to load order sheet.');
    } finally {
      setLoading(false);
    }
  }, [endpoints, id, request]);

  useEffect(() => {
    loadOrderSheet();
  }, [loadOrderSheet]);

  const update = (field, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateNumber = (field, value) => {
    update(field, Number(value) || 0);
  };

  const calculateRebate = () => {
    const rebateAmount = (form.pbInvoicePrice * form.commissionablePercent / 100) * (form.rebatePercent / 100);
    update('rebateAmount', Math.round(rebateAmount * 100) / 100);
  };

  const calculateCommission = (percentField, amountField) => {
    const commissionAmount = (form.pbInvoicePrice * form.commissionablePercent / 100) * (form[percentField] / 100);
    update(amountField, Math.round(commissionAmount * 100) / 100);
  };

  const handleSave = async () => {
    if (!form?.id) return;
    setSaving(true);
    setSaved(false);
    setPageError('');
    try {
      await request({ method: 'PUT', url: endpoints.salesQuoteOrders.base, data: toApiPayload(form) });
      setSaved(true);
    } catch {
      setPageError('Unable to save order sheet.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-order-sheet-title">
      <Row className="mb-3">
        <Col><h4 id="sales-order-sheet-title">Order Sheet</h4></Col>
        <Col className="text-end">
          <Button as={Link} to="/sales/order-sheets" variant="secondary" className="me-2">Back</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading || !form}>{saving ? 'Saving...' : 'Save'}</Button>
        </Col>
      </Row>

      {saved && <Alert variant="success">Order sheet saved.</Alert>}
      {pageError && <Alert variant="danger">{pageError}</Alert>}
      {loading ? (
        <div className="card"><div className="card-body"><Spinner animation="border" size="sm" /> Loading order sheet...</div></div>
      ) : form && (
        <Accordion defaultActiveKey={['0', '1', '2', '3']} alwaysOpen className="sales-order-sheet-accordion mb-3">
          <Accordion.Item eventKey="0" className="mb-2">
            <Accordion.Header><span className="mx-auto">General Info</span></Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label className="fw-semibold">Project Name</Form.Label>
                  <div>
                    <Link to={`/sales/salesquote/${form.projectId}/details/${form.saleQuoteId}`}>
                      {form.projectName || '-'}
                    </Link>
                  </div>
                </Col>
                <Col md={6}>
                  <Form.Label className="fw-semibold">Job Number</Form.Label>
                  <Form.Control size="sm" value={form.jobNumber} onChange={(event) => update('jobNumber', event.target.value)} />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label className="fw-semibold">Drawing</Form.Label>
                  <Form.Select size="sm" value={form.drawing} onChange={(event) => updateNumber('drawing', event.target.value)}>
                    <option value={1}>Approved</option>
                    <option value={0}>Pending</option>
                    <option value={2}>N/A</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">Witness Test Required</Form.Label>
                  <Form.Select size="sm" value={form.witnessTest} onChange={(event) => updateNumber('witnessTest', event.target.value)}>
                    <option value={1}>Yes</option>
                    <option value={0}>No</option>
                    <option value={2}>TBD</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">Late Penalty</Form.Label>
                  <div className="d-flex align-items-center gap-3">
                    <Form.Check type="radio" label="Yes" checked={form.latePenalty} onChange={() => update('latePenalty', true)} />
                    <Form.Check type="radio" label="No" checked={!form.latePenalty} onChange={() => update('latePenalty', false)} />
                    {form.latePenalty && (
                      <Form.Control size="sm" type="number" value={form.penaltyAmount} onChange={(event) => updateNumber('penaltyAmount', event.target.value)} style={{ width: 120 }} />
                    )}
                  </div>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label className="fw-semibold">Order Date</Form.Label>
                  <Form.Control size="sm" type="date" value={form.orderDate} onChange={(event) => update('orderDate', event.target.value)} />
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">Delivery Date</Form.Label>
                  <Form.Control size="sm" type="date" value={form.isSchedule ? '' : form.requestedDeliveryDate} disabled={form.isSchedule} onChange={(event) => update('requestedDeliveryDate', event.target.value)} />
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Form.Check label="Per Schedule" checked={form.isSchedule} onChange={(event) => update('isSchedule', event.target.checked)} />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label className="fw-semibold">Market</Form.Label>
                  <div className="d-flex gap-3 flex-wrap">
                    {marketOptions.map((option) => (
                      <Form.Check key={option.value} type="radio" label={option.label} checked={form.market === option.value} onChange={() => update('market', option.value)} />
                    ))}
                  </div>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="1" className="mb-2">
            <Accordion.Header><span className="mx-auto">Invoice Address</span></Accordion.Header>
            <Accordion.Body>
              <Row>
                {addressFields.map(([field, label]) => (
                  <Col md={6} key={field} className="mb-2">
                    <Form.Label className="fw-semibold">{label}</Form.Label>
                    <Form.Control size="sm" value={form[field]} onChange={(event) => update(field, event.target.value)} />
                  </Col>
                ))}
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="2" className="mb-2">
            <Accordion.Header><span className="mx-auto">Shipment Details</span></Accordion.Header>
            <Accordion.Body>
              <Row>
                {shipToFields.map(([field, label]) => (
                  <Col md={6} key={field} className="mb-2">
                    <Form.Label className="fw-semibold">{label}</Form.Label>
                    <Form.Control size="sm" value={form[field]} onChange={(event) => update(field, event.target.value)} />
                  </Col>
                ))}
                <Col xs={12}>
                  <Form.Label className="fw-semibold">Special Instructions</Form.Label>
                  <Form.Control as="textarea" rows={3} value={form.specialInstructions} onChange={(event) => update('specialInstructions', event.target.value)} />
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="3" className="mb-2">
            <Accordion.Header><span className="mx-auto">Price Breakdown</span></Accordion.Header>
            <Accordion.Body>
              <Row>
                {priceFields.map(([field, label]) => (
                  <Col md={4} key={field} className="mb-2">
                    <Form.Label className="fw-semibold">{label}</Form.Label>
                    <Form.Control size="sm" type="number" value={form[field]} onChange={(event) => updateNumber(field, event.target.value)} />
                  </Col>
                ))}
              </Row>
              <hr />
              <Row className="align-items-end">
                <Col md={3} className="mb-2">
                  <Form.Label className="fw-semibold">Commissionable Percent</Form.Label>
                  <Form.Control size="sm" type="number" value={form.commissionablePercent} onChange={(event) => updateNumber('commissionablePercent', event.target.value)} />
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="fw-semibold">Rebate Percent</Form.Label>
                  <Form.Control size="sm" type="number" value={form.rebatePercent} onChange={(event) => updateNumber('rebatePercent', event.target.value)} />
                </Col>
                <Col md={3} className="mb-2"><Button variant="primary" onClick={calculateRebate}>Calculate Rebate</Button></Col>
                <Col md={3} className="mb-2 fw-semibold">Rebate: {formatCurrency(form.rebateAmount)}</Col>
              </Row>
              <Row className="align-items-end">
                <Col md={3} className="mb-2"><Form.Label className="fw-semibold">Commission 1 Name</Form.Label><Form.Control size="sm" value={form.commission1Name} onChange={(event) => update('commission1Name', event.target.value)} /></Col>
                <Col md={3} className="mb-2"><Form.Label className="fw-semibold">Commission 1 Percent</Form.Label><Form.Control size="sm" type="number" value={form.commission1Percent} onChange={(event) => updateNumber('commission1Percent', event.target.value)} /></Col>
                <Col md={3} className="mb-2"><Button variant="primary" onClick={() => calculateCommission('commission1Percent', 'commission1Amount')}>Calculate</Button></Col>
                <Col md={3} className="mb-2 fw-semibold">Amount: {formatCurrency(form.commission1Amount)}</Col>
              </Row>
              <Row className="align-items-end">
                <Col md={3} className="mb-2"><Form.Label className="fw-semibold">Commission 2 Name</Form.Label><Form.Control size="sm" value={form.commission2Name} onChange={(event) => update('commission2Name', event.target.value)} /></Col>
                <Col md={3} className="mb-2"><Form.Label className="fw-semibold">Commission 2 Percent</Form.Label><Form.Control size="sm" type="number" value={form.commission2Percent} onChange={(event) => updateNumber('commission2Percent', event.target.value)} /></Col>
                <Col md={3} className="mb-2"><Button variant="primary" onClick={() => calculateCommission('commission2Percent', 'commission2Amount')}>Calculate</Button></Col>
                <Col md={3} className="mb-2 fw-semibold">Amount: {formatCurrency(form.commission2Amount)}</Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </section>
  );
};

export default OrderSheetDetail;
